import { useState } from "react";
import AppShell from "../../components/app/AppShell";
import { Eyebrow, HelperText } from "../../components/design";
import { STUB_INVOICES } from "../../lib/stubs";

// /app/buchhaltung — Mail-form for "An Buchhaltung schreiben".
// Reference: extras.jsx PageBuchhaltung.

const TOPICS: { key: string; label: string }[] = [
  { key: "rechnung", label: "Rechnungskorrektur" },
  { key: "anschrift", label: "Anschriftsänderung" },
  { key: "ust", label: "USt-Anpassung" },
  { key: "sonstiges", label: "Sonstiges" },
];

export default function AppBuchhaltung() {
  const [topic, setTopic] = useState("rechnung");
  const [invoiceNr, setInvoiceNr] = useState(STUB_INVOICES[0]?.nr || "");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // TODO(Phase C): wire to email backend (route into support inbox tagged with inv:<R-...>).
    setSent(true);
  }

  return (
    <AppShell
      eyebrow="An die Buchhaltung"
      title={
        <>
          An Buchhaltung <em>schreiben</em>.
        </>
      }
      backTo="/app/invoices"
    >
      <form onSubmit={onSubmit} style={{ padding: "0 22px 90px" }}>
        <HelperText>
          Wir antworten meistens innerhalb 4 Std.
        </HelperText>

        {sent && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: "rgba(95,107,79,0.12)",
              border: "1px solid rgba(95,107,79,0.30)",
              borderLeft: "3px solid var(--sage)",
              borderRadius: 4,
              padding: "10px 14px",
              color: "var(--sage)",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              marginBottom: 18,
            }}
          >
            Nachricht versendet — wir melden uns bei Ihnen.
          </div>
        )}

        {/* Topic */}
        <Eyebrow>Worum geht's?</Eyebrow>
        <div
          role="radiogroup"
          aria-label="Thema"
          style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}
        >
          {TOPICS.map((o) => {
            const sel = o.key === topic;
            return (
              <label
                key={o.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  background: "var(--paper-50)",
                  border: sel
                    ? "1.5px solid var(--oxblood)"
                    : "1px solid rgba(43,26,15,0.12)",
                  borderRadius: 10,
                  boxShadow: sel ? "0 4px 10px rgba(107,32,24,0.08)" : "none",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="topic"
                  value={o.key}
                  checked={sel}
                  onChange={() => setTopic(o.key)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    flexShrink: 0,
                    border: sel
                      ? "5px solid var(--oxblood)"
                      : "1.5px solid rgba(43,26,15,0.25)",
                    background: sel ? "var(--paper-50)" : "transparent",
                    boxSizing: "border-box",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontFamily: "var(--f-display)",
                    fontSize: 15,
                    color: "var(--ink)",
                  }}
                >
                  {o.label}
                </span>
              </label>
            );
          })}
        </div>

        {/* Reference invoice */}
        <Eyebrow>Bezogene Rechnung</Eyebrow>
        <select
          value={invoiceNr}
          onChange={(e) => setInvoiceNr(e.target.value)}
          aria-label="Bezogene Rechnung"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.12)",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 18,
            fontFamily: "var(--f-display)",
            fontSize: 14,
            color: "var(--ink)",
          }}
        >
          {STUB_INVOICES.map((inv) => (
            <option key={inv.nr} value={inv.nr}>
              {inv.nr} · {inv.title} · {inv.total}
            </option>
          ))}
        </select>

        {/* Message */}
        <Eyebrow>Ihre Nachricht</Eyebrow>
        <textarea
          rows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Hallo zusammen, …"
          aria-label="Nachricht"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.14)",
            borderRadius: 10,
            padding: "14px",
            fontFamily: "var(--f-body)",
            fontSize: 15,
            lineHeight: 1.55,
            color: "var(--ink)",
            resize: "vertical",
            minHeight: 160,
            marginBottom: 18,
          }}
        />

        <button
          type="submit"
          className="btn-primary"
          style={{ width: "100%", boxSizing: "border-box" }}
        >
          Nachricht senden
        </button>

        <div
          style={{
            textAlign: "center",
            marginTop: 12,
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12,
            color: "var(--ink-faint)",
          }}
        >
          Direkter Kontakt:{" "}
          <a
            href="mailto:kontakt@tino247.de"
            style={{ color: "var(--ink-soft)", textDecoration: "underline" }}
          >
            kontakt@tino247.de
          </a>
        </div>
      </form>
    </AppShell>
  );
}
